import {t} from 'i18next'
import {Button} from 'primereact/button'
import {Column} from 'primereact/column'
import {DataTable} from 'primereact/datatable'
import {Dropdown} from 'primereact/dropdown'
import {Tooltip} from 'primereact/tooltip'
import React, {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {API_EDUCATION_REPORTS} from '../../../../../constants'
import {EducationReportDb} from '../../../../../model/enums/educationReport.model'
import {RequestMethod} from '../../../../../model/enums/enums'
import {Group} from '../../../../../model/group.model'
import {Apprentice} from '../../../../../model/person.model'
import {Profession} from '../../../../../model/profession.model'
import {useAppSelector} from '../../../../../redux/hooks'
import {AuthRoutes} from '../../../../../router/routes'
import {handleRequest} from '../../../../../services/api/base/handleRequest'
import {findApprenticeById} from '../../../../../util/arraySearch'
import {getActiveSemesterOfApprentice} from '../../../../../util/getActiveSemester'
import {AvatarCell} from '../../../../util/data-table/cell-body/FullNameApprenticeBody'
import {InactiveRowStyleByObjectAndPerson} from '../../../../util/data-table/row-class/InactiveRow'
import {
  sortApprenticesByEdrDossier,
  sortProfessionFromEdrDossier
} from '../../../../util/sort-functions/TableSortFunctions'

// needs export for sort function
export interface ApprenticeDossier {
  id_apprentice: number
  apprentice: Apprentice | undefined
  activeSemester: number
  numSemesters: number
  isActive: boolean
  profession_id: number | undefined
  // must be of size 8
  educationReports: EducationReportDb[]
}

interface FilterProps {
  showOnlyActiveApprenticeship: boolean
  selectedSemesterId: number
  selectedGroupId: number
  selectedProfessionId: number
}

const defaultFilter = {
  showOnlyActiveApprenticeship: true,
  selectedSemesterId: -1,
  selectedGroupId: -1,
  selectedProfessionId: -1,
}

export const EducationReportList = (): JSX.Element => {
  const [apprenticeDossiers, setApprenticeDossiers] = useState<
    ApprenticeDossier[]
  >([])
  const [filteredDossiers, setFilteredDossiers] = useState<ApprenticeDossier[]>(
    []
  )
  const [loading, setLoading] = useState<boolean>(true)

  const [filterOptions, setFilterOptions] = useState<FilterProps>(defaultFilter)

  const [showOnlyActiveDossiers, setShowOnlyActiveDossiers] =
    useState<boolean>(true)
  const [selectedSemester, setSelectedSemester] = useState<number>(-1)
  const [selectedProfession, setSelectedProfession] = useState<number>(-1)
  const [selectedGroup, setSelectedGroup] = useState<number>(-1)
  const [groupMembers, setGroupMembers] = useState<Map<number, number[]>>(
    new Map()
  )
  const apprentices = useAppSelector((state) => state.apprentices)
  const professions = useAppSelector((state) => state.professions)

  useEffect(() => {
    setFilterOptions({
      showOnlyActiveApprenticeship: showOnlyActiveDossiers,
      selectedSemesterId: selectedSemester,
      selectedGroupId: selectedGroup,
      selectedProfessionId: selectedProfession,
    })
  }, [
    showOnlyActiveDossiers,
    selectedSemester,
    selectedProfession,
    selectedGroup,
  ])

  useEffect(() => {
    let res = apprenticeDossiers

    if (filterOptions.selectedGroupId !== -1) {
      res = []
      const members = groupMembers.get(filterOptions.selectedGroupId)
      if (members) {
        for (let personId of members) {
          const member = apprenticeDossiers.find(
            (e) => e.apprentice?.person_id === personId
          )
          if (member) {
            res.push(member)
          }
        }
      }
    }

    if (filterOptions.showOnlyActiveApprenticeship) {
      res = res.filter((e) => e.isActive)

      // filter to get only the active apprentices
      res = res.filter((e) => e.apprentice?.is_active === 1)
    }

    if (filterOptions.selectedSemesterId !== -1) {
      res = res.filter(
        (e) => e.activeSemester === filterOptions.selectedSemesterId
      )
    }
    if (filterOptions.selectedProfessionId !== -1) {
      res = res.filter(
        (e) =>
          e.apprentice?.apprentice?.id_profession ===
          filterOptions.selectedProfessionId
      )
    }

    setFilteredDossiers(res)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions])

  const navigate = useNavigate()

  // used for filter
  const groups = useAppSelector((state) => state.groups)

  const dummyGroup: Group = {
    group_id: -1,
    group_name: t<string>('hrm.group.all'),
    is_active: 1,
  }

  // returns the length of an apprenticeship in numbers of semesters
  // possible values should be 4, 6, 8
  // in case of missing values we assume 8
  const getNumSemesters = (apprentice: Apprentice): number => {
    if (
      apprentice.apprentice &&
      apprentice.apprentice.contract_start &&
      apprentice.apprentice.contract_end
    ) {
      const start_year = new Date(
        apprentice.apprentice.contract_start
      ).getFullYear()
      const end_year = new Date(
        apprentice.apprentice.contract_end
      ).getFullYear()
      if (start_year && end_year) return (end_year - start_year) * 2
    }
    return 8
  }

  const isActive = (apprentice: Apprentice): boolean => {
    if (
      apprentice.apprentice &&
      apprentice.apprentice.contract_start &&
      apprentice.apprentice.contract_end
    ) {
      const today = new Date()
      return (
        new Date(apprentice.apprentice.contract_start) <= today &&
        new Date(apprentice.apprentice.contract_end) >= today
      )
    } else {
      return false
    }
  }

  useEffect(() => {
    // collect all apprentice dossiers
    const setupApprenticeDossiers = async () => {
      const apprenticesInApprenticeship = apprentices

      const temp: ApprenticeDossier[] = []

      apprenticesInApprenticeship.forEach((a: Apprentice) => {
        if (a.apprentice?.apprentice_id) {
          const currentSemester = getActiveSemesterOfApprentice(a)
          const apprenticeDuration = getNumSemesters(a)
          temp.push({
            id_apprentice: a.apprentice?.apprentice_id,
            apprentice: a,
            activeSemester: currentSemester,
            numSemesters: apprenticeDuration,
            isActive: isActive(a),
            educationReports: new Array<EducationReportDb>(8),
            profession_id: a.apprentice?.id_profession,
          })
        }
      })
      // fetch reports
      const result = await handleRequest<EducationReportDb[]>(
        API_EDUCATION_REPORTS(),
        RequestMethod.GET
      ).catch((error: any) => {
        console.warn(
          '[Education Report Module] Error occurred loading the reports',
          error
        )
      })

      // here we add the necessary information of the report objects to our object structure
      if (result?.data && result.data.length > 0) {
        result.data.forEach((report: EducationReportDb) => {
          const existing = temp.find(
            (e) => e.id_apprentice === report.id_apprentice
          )
          if (existing && report.semester_report) {
            // undefined if it does not exist
            existing.educationReports[report.semester_report - 1] = report
          }
        })
      }
      setApprenticeDossiers(temp)
      // apply filter to only show active per default
      let res: ApprenticeDossier[] = [...temp]
      if (showOnlyActiveDossiers) {
        res = temp.filter((e) => e.isActive && e.apprentice?.is_active === 1)
      }
      setFilteredDossiers(res)
    }

    setupApprenticeDossiers().finally(() => {
      console.log('[Education Report Module] Finished creating the reports.')
      setLoading(false)
    })
    // TODO detect change on education report entry
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apprentices])

  useEffect(() => {
    const handleGroups = async () => {
      let groupMap = groupMembers
      for (let group of groups) {
        if (group?.group_member) {
          for (const member of group.group_member) {
            if (member.id_person) {
              if (groupMap.has(group.group_id)) {
                // workaround for unsolved problem of receiving the group twice sometimes
                if (!groupMap.get(group.group_id)?.includes(member.id_person)) {
                  groupMap.get(group.group_id)?.push(member.id_person)
                }
              } else {
                groupMap.set(group.group_id, [member.id_person])
              }
            }
          }
        }
      }
      setGroupMembers(groupMap)
    }

    handleGroups().then(() => {
      console.log('[Education Report Module] Finished adding the groups.')
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups])

  // returns the avatar of the apprentice
  const apprenticeAvatar = (apprentice: ApprenticeDossier) => {
    const app = findApprenticeById(apprentice.id_apprentice, apprentices)
    return AvatarCell(app)
  }

  const routeToEducationReport = (
    _apprentice: ApprenticeDossier,
    _semester: number
  ) => {
    const route = `${AuthRoutes.educationReports}/${_apprentice.id_apprentice}/${_semester}`
    navigate(route)
  }

  const getActionButton = (apprentice: ApprenticeDossier, semester: number) => {
    let actionElement: JSX.Element = <></>
    let styleClass: string = ''
    const edReport = apprentice.educationReports[semester - 1]

    // here we want a grey cell

    if (apprentice.numSemesters < semester) {
      styleClass = 'inactive-cell'
    }
    //else if (semester > apprentice.activeSemester && !(apprentice.activeSemester === -1)) {
    //	styleClass = 'future-cell'
    //}
    else if (edReport?.signed_file_reference) {
      actionElement = (
        <Button
          className="p-button-text icon-green"
          icon={'fa-solid fa-check-circle '}
          onClick={(_) => routeToEducationReport(apprentice, semester)}
        />
      )
      if (semester === apprentice.activeSemester) {
        styleClass = 'current-semester-cell'
      }
    } else if (semester === apprentice.activeSemester) {
      actionElement = (
        <Button
          className="p-button-text"
          icon={'fa-solid fa-plus'}
          onClick={(_) => routeToEducationReport(apprentice, semester)}
        />
      )

      styleClass = 'current-semester-cell'
    }
    // we also want to show the correct symbol if the apprenticeship is already over
    else if (
      semester < apprentice.activeSemester ||
      apprentice.activeSemester === -1
    ) {
      const existing = apprentice.educationReports[semester]

      actionElement = (
        <Button
          className={`p-button-text ${existing ? 'icon-orange' : 'icon-red'}`}
          icon={`fa-solid ${
            existing ? 'fa-exclamation-triangle' : 'fa-exclamation-triangle'
          } `}
          onClick={(_) => routeToEducationReport(apprentice, semester)}
        />
      )
    }

    return (
      <div className={styleClass}>
        {' '}
        &nbsp;
        {actionElement}
      </div>
    )
  }

  const columns = Array.from({ length: 8 }, (_, i) => i + 1).map((v, _) => {
    return (
      <Column
        style={{ position: 'relative' }}
        key={v}
        header={'S' + v}
        field={'semester'}
        body={(apprentice) => getActionButton(apprentice, v)}
        align={'center'}
        dataType={'numeric'}
      ></Column>
    )
  })

  const semesterOptionTemplate = (semesterId: number) => {
    if (semesterId === -1) {
      return t<string>('core.education.all_semester')
    }
    return t<string>('education_report.active_semester', {
      activeSemester: semesterId,
    })
  }

  const semesterItems = [-1, 1, 2, 3, 4, 5, 6, 7, 8]

  const activeLanguage = localStorage.getItem('i18nextLng')

  const professionsDropdownOptions = () => {
    let profs = professions?.map((p: Profession) => {
      return {
        label:
          activeLanguage === 'de'
            ? p.profession
            : t(`profession.${p.profession_id}`),
        value: p.profession_id,
      }
    })

    profs.unshift({
      label: t<string>('profession.all_professions'),
      value: -1,
    })
    return profs
  }

  const resetFilter = () => {
    setShowOnlyActiveDossiers(true)
    setSelectedProfession(-1)
    setSelectedGroup(-1)
    setSelectedSemester(-1)
  }

  return (
    <div className={'card'}>
      <div className={'formgrid grid gap-2 mb-2 ml-0 mr-1'}>
        <div>
          <Dropdown
            value={showOnlyActiveDossiers}
            className={'min-width-240'}
            options={[
              { value: false, label: t<string>('core.show_all') },
              {
                value: true,
                label: t<string>('core.show_active'),
              },
            ]}
            onChange={(e) => setShowOnlyActiveDossiers(!showOnlyActiveDossiers)}
          />
        </div>
        <Dropdown
          value={selectedProfession}
          options={professionsDropdownOptions()}
          placeholder={t<string>('education_report.show_all_professions')}
          onChange={(e) => setSelectedProfession(e.target.value)}
          filter
          showFilterClear={true}
          style={{ maxWidth: '80%' }}
          className="col"
        />

        <Dropdown
          onChange={(e) => setSelectedGroup(e.target.value.group_id)}
          placeholder={t<string>('hrm.group.all')}
          value={
            selectedGroup === -1
              ? dummyGroup
              : groups.find((g) => g.group_id === selectedGroup)
          }
          options={[dummyGroup].concat(groups)}
          showFilterClear={true}
          optionLabel="group_name"
          filter
          filterBy="group_name"
          className={'col'}
        />
        <Dropdown
          placeholder={t<string>('core.education.all_semester')}
          value={selectedSemester}
          itemTemplate={semesterOptionTemplate}
          valueTemplate={semesterOptionTemplate}
          options={semesterItems}
          onChange={(e) => {
            setSelectedSemester(e.target.value)
          }}
          className={'col'}
        />
        <Button
          icon={'pi pi-filter-slash'}
          className={'p-button-text col-1'}
          tooltip={t<string>('core.reset_filter')}
          tooltipOptions={{ position: 'left' }}
          onClick={() => resetFilter()}
        />
      </div>
      <DataTable
        showGridlines
        stripedRows
        loading={loading}
        className={'p-datatable-sm ml-0 pl-0'}
        value={filteredDossiers}
        rowHover
        sortField={'activeSemester'}
        sortOrder={-1}
        sortMode="single"
        removableSort
        scrollHeight={'calc(100vh - 260px)'}
        emptyMessage={t<string>('core.empty_message')}
        rowClassName={(row) =>
          InactiveRowStyleByObjectAndPerson(row, row.id_apprentice, apprentices)
        }
      >
        <Column
          header={
            t<string>('core.apprentices') +
            ' ' +
            filteredDossiers.length +
            '/' +
            apprenticeDossiers.length
          }
          field={'id_apprentice'}
          filterField={'id_apprentice'}
          body={apprenticeAvatar}
          sortable
          sortFunction={sortApprenticesByEdrDossier}
          dataType={'numeric'}
        />
        <Column
          header={t<string>('core.field_of_profession')}
          body={(e: ApprenticeDossier) => {
            if (e?.profession_id) {
              const p = professions.find(
                (prof) => prof.profession_id === e.profession_id
              )
              return activeLanguage === 'de'
                ? p.profession
                : t(`profession.${p.profession_id}`)
            }
            return ''
          }}
          field={'profession_id'}
          filterField={'profession_id'}
          sortable
          sortFunction={(e) => sortProfessionFromEdrDossier(e, professions)}
          dataType={'numeric'}
        />
        {columns}
        <Column
          sortField={'activeSemester'}
          filterField={'activeSemester'}
          sortable
          dataType={'numeric'}
          headerClassName={'active-semester-sort-header'}
          bodyStyle={{
            backgroundColor: 'rgb(226, 227,227)',
            borderColor: 'rgb(226, 227,227)',
          }}
        />
      </DataTable>
      <div
        className={'flex justify-content-end align-items-end w-full pb-4 mt-2'}
      >
        <span
          className={'fa-solid fa-info-circle icon-blue mt-2 pr-1'}
          style={{ zIndex: 200 }}
        />
      </div>
      <Tooltip
        target={'.fa-info-circle'}
        className={'legend-tooltip'}
        style={{ width: '90%' }}
        position={'top'}
      >
        <div className={'p-2 gap-1 small-text'}>
          <h4>
            <b>{t<string>('core.explanation')}</b>
          </h4>
          <p>
            <span className={'icon-light-blue fa-solid fa-square fa-lg mr-2'} />
            <span>{t<string>('core.education.active_semester')}</span>
          </p>
          <p className={'pt-2'}>
            <span
              className={'icon-green fa-solid fa-check-circle fa-lg mr-2'}
            />
            <span>{t<string>('education_report.finished')}</span>
          </p>

          <p className={'pt-2'}>
            <span className={'fa-solid fa-plus icon-blue fa-lg mr-2'} />
            <span>{t<string>('education_report.create')}</span>
            <span>{t<string>('education_report.edit')}</span>
          </p>
          <p className={'pt-2'}>
            <span
              className={
                'icon-orange fa-solid fa-exclamation-triangle fa-lg mr-2'
              }
            />
            <span>{t<string>('education_report.incomplete')}</span>
          </p>
          <p className={'pt-2'}>
            <span
              className={'icon-red fa-solid fa-exclamation-triangle fa-lg mr-2'}
            />
            <span>{t<string>('education_report.missing')}</span>
          </p>
        </div>
      </Tooltip>
      <Tooltip
        content={t<string>('education_report.sort_by_active_semester')}
        target={'.active-semester-sort-header'}
        position={'left'}
      />
    </div>
  )
}